/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Button } from '@carbon/react';

import { mapClusterInfo } from 'App/Pages/Diagram/features/deployment/actions/map-cluster-info';
import { StateIndicator } from 'App/Pages/Diagram/features/deployment/actions/state-indicator';
import { deploymentTypes, deploymentStates as states } from 'App/Pages/Diagram/features/deployment/utils';
import { notificationStore } from 'components/NotificationSystem';
import { formLinkStore } from 'App/Pages/Diagram/FormLinking';

import runService from './RunService';

export default function StartInstance({
  selectedCluster,
  tenantId,
  clientId,
  clientSecret,
  diagramId,
  processApplicationId,
  closeModal,
  deploymentType,
  jsonPayload,
  isJsonPayloadValid,
  currentState,
  transitionToRunningState,
  transitionToRunFailedState,
  disabled,
  isInputValidationSuccessful,
  enableValidation
}) {
  const startInstance = async () => {
    if (selectedCluster?.authentication === 'CLIENT_CREDENTIALS' && !isInputValidationSuccessful) {
      enableValidation();
      return;
    }
    if (!isJsonPayloadValid) {
      return;
    }

    const clusterInfo = mapClusterInfo(selectedCluster, tenantId, clientId, clientSecret);

    try {
      transitionToRunningState();
      if (deploymentType === deploymentTypes.DIAGRAM) {
        await runService.runDiagram(diagramId, clusterInfo, jsonPayload, formLinkStore.formResolutions);
      } else if (deploymentType === deploymentTypes.PROCESS_APPLICATION) {
        await runService.runProcessApplication(
          processApplicationId,
          clusterInfo,
          jsonPayload,
          formLinkStore.formResolutions
        );
      }
      notificationStore.showSuccess('Instance started!');
      closeModal();
    } catch (e) {
      transitionToRunFailedState(e?.[0]?.detail ?? '');
    }
  };

  const renderStateIndicator = () => {
    switch (currentState) {
      case states.RUN_FAILED:
        return <StateIndicator text="Failed" status="error" />;
      case states.RUNNING:
        return <StateIndicator text="Running" status="active" />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderStateIndicator() || (
        <Button onClick={startInstance} type="submit" data-modal-primary-focus disabled={disabled}>
          Start instance
        </Button>
      )}
    </>
  );
}
